import { useEffect, useRef, useState } from 'react'

import { Avatar, Menu, TextInput, Tooltip } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'

import { navigate, routes } from '@redwoodjs/router'
import { useMutation, useQuery } from '@redwoodjs/web'

import AdminContainer from 'src/components/AdminContainer/AdminContainer'
import AddCompanyModal from 'src/components/Overlays/AddCompanyModal/AddCompanyModal'
import StyledMenu from 'src/components/Overlays/StyledMenu/StyledMenu'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_MY_COMPANIES,
  UPDATE_CURRENT_EMPLOYEE_ID,
} from 'src/graphql/users.graphql'
import IconAdd from 'src/icons/IconAdd'
import IconCheckmark from 'src/icons/IconCheckmark'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconSearch from 'src/icons/IconSearch'

const defaultCompany = {
  companyName: 'Double Nickel',
  employeeId: '',
}

const COMPANY_POLL_INTERVAL = 30_000

const SidebarCompanyMenu = ({ sidebarExpanded }) => {
  const [selectedCompany, setSelectedCompany] = useState(defaultCompany)
  const [searchText, setSearchText] = useState('')
  const [addModalOpened, addModalHandlers] = useDisclosure(false)
  const knownEmployeeIdRef = useRef<string | null>(null)

  const companyNameLower = selectedCompany.companyName?.toLowerCase() ?? ''
  const isInternalAccount =
    companyNameLower.includes('double nickel') ||
    companyNameLower.includes('doublenickel')

  const { data: { getMyCompanies: companies } = {}, refetch } = useQuery(
    GET_MY_COMPANIES,
    {
      pollInterval: COMPANY_POLL_INTERVAL,
      onCompleted: (data) => {
        const currentCompany = data.getMyCompanies.find(
          (company) => company.isCurrentEmployeeId
        )

        const newEmployeeId = currentCompany?.employeeId ?? null

        // Detect server-side company context change (e.g., employee removed)
        if (
          knownEmployeeIdRef.current &&
          newEmployeeId &&
          knownEmployeeIdRef.current !== newEmployeeId
        ) {
          window.location.reload()
          return
        }

        knownEmployeeIdRef.current = newEmployeeId
        setSelectedCompany(currentCompany ?? defaultCompany)
      },
      onError: () => {
        toast('Unable to get companies, please try again.', 'error')
      },
    }
  )

  // Refetch immediately when window regains focus for faster detection
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refetch()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [refetch])

  const [updateCurrentEmployeeId] = useMutation(UPDATE_CURRENT_EMPLOYEE_ID, {
    onCompleted: (data) => {
      toast('Company changed successfully', 'success')
      const urls = [
        '/',
        '/applicants',
        '/jobs',
        '/voe-requests',
        '/settings',
        '/analytics',
        '/verification-analytics',
        '/activities',
        '/communications',
        '/communications/calls',
        '/communications/emails',
        '/activity-analytics/calls',
        '/activity-analytics/sms',
        '/tasks',
        '/calendar',
        '/expirations',
      ]
      const currentUrl = window.location.pathname
      if (data?.updateCurrentEmployeeId) {
        if (!urls.includes(currentUrl)) {
          navigate(routes.dashboard())
          window.location.reload()
        } else {
          window.location.reload()
        }
      }
    },
    onError: () => {
      toast('Unable to switch companies. Please try again.', 'error')
    },
  })

  const filteredCompanies = companies?.filter((company) =>
    company.companyName.toLowerCase().includes(searchText.toLowerCase())
  )

  return (
    <StyledMenu offset={1} width={250} withArrow={!sidebarExpanded}>
      <Menu.Target>
        <div
          className={`flex h-12 w-full flex-row items-center justify-center  rounded-lg py-2   ${
            sidebarExpanded
              ? `${isInternalAccount ? 'bg-doubleNickel-gray-400' : 'bg-white'} px-2 shadow hover:shadow-md`
              : ''
          }`}
        >
          {sidebarExpanded ? (
            <div className="flex w-full flex-row items-center justify-start gap-2">
              <Avatar
                color={isInternalAccount ? 'dark' : 'blue'}
                radius={'sm'}
                size={24}
                variant="filled"
              >
                {selectedCompany.companyName[0]}
              </Avatar>
              <div className="flex w-[70%] flex-col justify-center overflow-auto text-nowrap">
                <div className="text-xs font-semibold text-doubleNickel-gray-800">
                  {selectedCompany.companyName}
                </div>
                <div className="text-xs text-doubleNickel-gray-600">
                  via Double Nickel
                </div>
              </div>
              <div className="ml-auto flex flex-col items-center justify-center">
                <IconChevronDown className="h-3.5 w-3.5 rotate-180 fill-none stroke-doubleNickel-gray-500" />
                <IconChevronDown className=" h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-500" />
              </div>
            </div>
          ) : (
            <Tooltip label={selectedCompany.companyName} position="right" withArrow>
              <Avatar
                color={isInternalAccount ? 'dark' : 'blue'}
                radius="sm"
                size={32}
                variant="filled"
                className="shadow-md hover:shadow-lg"
              >
                {selectedCompany.companyName[0]}
              </Avatar>
            </Tooltip>
          )}
        </div>
      </Menu.Target>
      <Menu.Dropdown className="overflow-hidden">
        <AdminContainer>
          <TextInput
            className="mb-2"
            placeholder="Search companies"
            leftSection={
              <IconSearch className="h-4 fill-none stroke-doubleNickel-gray-600" />
            }
            radius="sm"
            size="sm"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </AdminContainer>

        <div className="flex max-h-[70vh] flex-col overflow-auto">
          {filteredCompanies?.map((company) => (
            <Menu.Item
              className="flex-none"
              key={company.companyName}
              onClick={() => {
                if (company.employeeId === selectedCompany.employeeId) return
                // Update ref before mutation to prevent race with poll/refetch
                // detecting this intentional switch as a server-side context change
                knownEmployeeIdRef.current = company.employeeId
                updateCurrentEmployeeId({
                  variables: {
                    input: {
                      currentEmployeeId: company.employeeId,
                    },
                  },
                })
              }}
            >
              <div className="flex flex-row items-center justify-between">
                {company.companyName}
                {selectedCompany.employeeId === company.employeeId && (
                  <IconCheckmark className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
                )}
              </div>
            </Menu.Item>
          ))}
        </div>

        {filteredCompanies?.length === 0 && (
          <div className="p-3 text-center text-sm text-doubleNickel-gray-600">
            No companies found
          </div>
        )}
        <AdminContainer>
          <Menu.Item
            onClick={addModalHandlers.open}
            leftSection={<IconAdd className="h-4 w-4" />}
          >
            Add Company
          </Menu.Item>
        </AdminContainer>
      </Menu.Dropdown>
      <AddCompanyModal
        opened={addModalOpened}
        close={addModalHandlers.close}
        refetch={refetch}
      />
    </StyledMenu>
  )
}

export default SidebarCompanyMenu
